import { useState, useEffect, useMemo } from "react";
import { Plus, Search, Users, Calendar, Receipt, TrendingUp } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { clientsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrency } from "@/lib/data";
import { Client } from "./types";
import ClientForm from "./components/ClientForm";
import ClientsTable from "./components/ClientsTable";
import ClientLedgerModal from "./components/ClientLedgerModal";

function StatCard({ label, value, icon: Icon, sub }: { label: string; value: string; icon: any; sub?: string }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-amber-600" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-stone-400 font-medium mb-0.5">{label}</div>
        <div className="text-xl font-bold text-stone-800 truncate">{value}</div>
        {sub && <div className="text-xs text-stone-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === "staff";

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [ledgerClient, setLedgerClient] = useState<Client | null>(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await clientsApi.list();
      setClients(response.data?.data || response.data || []);
    } catch {
      showToast("Failed to load clients", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) return clients;
    const q = searchQuery.toLowerCase();
    return clients.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.company_name?.toLowerCase().includes(q)
    );
  }, [clients, searchQuery]);

  const stats = useMemo(() => ({
    total: clients.length,
    withEvents: clients.filter(c => (c.events_count ?? 0) > 0).length,
    totalRevenue: clients.reduce((sum, c) => sum + (c.total_revenue || 0), 0),
    totalBalance: clients.reduce((sum, c) => sum + (c.balance || 0), 0),
  }), [clients]);

  const openAdd = () => { setEditingClient(null); setIsFormOpen(true); };
  const openEdit = (client: Client) => { setEditingClient(client); setIsFormOpen(true); };
  const closeForm = () => { setIsFormOpen(false); setEditingClient(null); };

  const openDelete = (client: Client) => {
    if ((client.events_count ?? 0) > 0) {
      showToast("Cannot delete a client with existing events", "error");
      return;
    }
    setDeletingClient(client);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingClient) return;
    setDeleteLoading(true);
    const prev = clients;
    setClients(c => c.filter(x => x.id !== deletingClient.id));
    try {
      await clientsApi.delete(deletingClient.id);
      showToast("Client deleted", "success");
      setDeleteModalOpen(false);
      setDeletingClient(null);
    } catch {
      setClients(prev);
      showToast("Failed to delete client", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: Partial<Client>) => {
    closeForm();
    try {
      if (editingClient) {
        const updated = { ...editingClient, ...data };
        setClients(c => c.map(x => x.id === editingClient.id ? updated : x));
        await clientsApi.update(editingClient.id, data);
        showToast("Client updated", "success");
      } else {
        const tempId = Date.now();
        const optimistic = { id: tempId, ...data, events_count: 0, total_revenue: 0, total_paid: 0, balance: 0 } as Client;
        setClients(c => [optimistic, ...c]);
        const response = await clientsApi.create(data);
        const created = response.data?.data || response.data;
        setClients(c => c.map(x => x.id === tempId ? created : x));
        showToast("Client added", "success");
      }
    } catch {
      showToast("Failed to save client", "error");
      loadData();
    }
  };

  const openLedger = (client: Client) => { setLedgerClient(client); setLedgerOpen(true); };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Clients</h1>
          <p className="text-sm text-stone-400 mt-0.5">Manage client profiles and track payment history</p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add Client
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total Clients" value={String(stats.total)} icon={Users} />
        <StatCard label="With Events" value={String(stats.withEvents)} icon={Calendar} sub={`${stats.total - stats.withEvents} inactive`} />
        <StatCard label="Total Revenue" value={formatCurrency(stats.totalRevenue)} icon={Receipt} />
        <StatCard label="Outstanding Balance" value={formatCurrency(stats.totalBalance)} icon={TrendingUp} />
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by name, phone, email, or company…"
          aria-label="Search clients"
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
        />
      </div>

      {/* Table */}
      <ClientsTable
        clients={filteredClients}
        isFiltered={!!searchQuery.trim()}
        isStaff={isStaff}
        onEdit={openEdit}
        onDelete={openDelete}
        onLedger={openLedger}
        onClearFilter={() => setSearchQuery("")}
        onAddClient={openAdd}
      />

      {/* Add / Edit modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={closeForm}
        title={editingClient ? "Edit Client" : "Add New Client"}
        size="md"
      >
        <ClientForm
          initialData={editingClient || undefined}
          onSubmit={handleFormSubmit}
          onCancel={closeForm}
        />
      </Modal>

      {/* Delete confirmation */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingClient(null); }}
        onConfirm={handleDelete}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        itemName={deletingClient?.name}
        loading={deleteLoading}
      />

      {/* Ledger modal */}
      <ClientLedgerModal
        client={ledgerClient}
        isOpen={ledgerOpen}
        onClose={() => { setLedgerOpen(false); setLedgerClient(null); }}
      />
    </DashboardLayout>
  );
}
